import { motion } from "framer-motion";
import { Palette, Server, Search, Sparkles } from "lucide-react";
import { useRef } from "react";

const services = [
  {
    icon: Palette,
    title: "Webdesign",
    desc: "Maßgeschneiderte, conversion-optimierte Designs mit klarer Bildsprache und ruhiger Typografie.",
    points: ["Wireframes & Konzept", "UI/UX in Figma", "Designsystem"],
  },
  {
    icon: Server,
    title: "Hosting & Betreuung",
    desc: "Schnelles Hosting in Deutschland, automatische Backups und laufende Pflege ohne Überraschungen.",
    points: ["SSL & CDN", "Monitoring", "Wartung inklusive"],
  },
  {
    icon: Search,
    title: "SEO & Sichtbarkeit",
    desc: "Technische Optimierung, sauberes Markup und lokale Sichtbarkeit – damit Kunden Sie finden.",
    points: ["On-Page SEO", "Core Web Vitals", "Lokale Optimierung"],
  },
  {
    icon: Sparkles,
    title: "Moderne Frontend-Erlebnisse",
    desc: "Subtile Animationen, smooth Scroll und Mikrointeraktionen – cinematisch, aber nicht aufdringlich.",
    points: ["Motion Design", "Performance-first", "Barrierearm"],
  },
];

function ServiceCard({ s, i }) {
  const ref = useRef(null);
  const onMouseMove = (e) => {
    const el = ref.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    el.style.setProperty("--mx", `${e.clientX - r.left}px`);
    el.style.setProperty("--my", `${e.clientY - r.top}px`);
  };

  const Icon = s.icon;

  return (
    <motion.div
      ref={ref}
      onMouseMove={onMouseMove}
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-80px" }}
      transition={{ duration: 0.7, delay: i * 0.08, ease: [0.16, 1, 0.3, 1] }}
      data-testid={`service-card-${i}`}
      className="spotlight-card group relative rounded-2xl border border-white/[0.08] bg-white/[0.02] backdrop-blur-sm p-7 hover:border-white/[0.18] transition-all duration-500 hover:-translate-y-1 hover:shadow-[0_20px_60px_-15px_rgba(59,130,246,0.18)]"
    >
      <div className="flex items-center justify-between">
        <div className="w-11 h-11 rounded-xl bg-white/[0.04] border border-white/[0.08] flex items-center justify-center group-hover:border-blue-500/40 group-hover:bg-blue-500/[0.06] transition-all duration-500">
          <Icon className="w-5 h-5 text-white group-hover:text-blue-300 transition-colors" />
        </div>
        <span className="text-[11px] font-mono text-zinc-600 tracking-wider">0{i + 1}</span>
      </div>

      <h3 className="mt-6 font-display text-xl text-white tracking-tight">
        {s.title}
      </h3>
      <p className="mt-3 text-sm leading-relaxed text-zinc-400">{s.desc}</p>

      <ul className="mt-6 space-y-1.5">
        {s.points.map((p) => (
          <li key={p} className="flex items-center gap-2 text-[13px] text-zinc-500">
            <span className="w-1 h-1 rounded-full bg-blue-400/70" />
            {p}
          </li>
        ))}
      </ul>

      {/* Corner accent */}
      <span className="pointer-events-none absolute top-0 right-0 h-px w-12 bg-gradient-to-l from-blue-500/40 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
      <span className="pointer-events-none absolute top-0 right-0 w-px h-12 bg-gradient-to-b from-blue-500/40 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
    </motion.div>
  );
}

export default function Services() {
  return (
    <section
      id="leistungen"
      data-testid="services-section"
      className="relative py-24 sm:py-32 border-t border-white/[0.05]"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-12 items-end mb-16">
          <div>
            <motion.span
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="inline-block text-[11px] uppercase tracking-[0.22em] text-zinc-500 font-semibold"
            >
              ↳ Leistungen
            </motion.span>
            <motion.h2
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
              className="mt-3 font-display text-3xl sm:text-5xl text-white tracking-tight leading-[1.05]"
            >
              Alles, was eine moderne <br /> Unternehmenswebsite braucht.
            </motion.h2>
          </div>
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7, delay: 0.1 }}
            className="text-base text-zinc-400 max-w-md leading-relaxed"
          >
            Wir verbinden klares Design, technische Sorgfalt und ein Auge für
            Details. Kein Baukasten, keine Templates – nur Websites, die zu
            Ihrem Unternehmen passen.
          </motion.p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          {services.map((s, i) => (
            <ServiceCard s={s} i={i} key={s.title} />
          ))}
        </div>
      </div>
    </section>
  );
}
